import Phaser from 'phaser';
import { TILE, BOMB_FUSE_MS, BOMB_EXPLODE_MS, MAP_COLS, MAP_ROWS } from '../constants';
import { CellType, BombState, type BombData, type ExplosionCell } from '../types';
import { events } from '../EventBus';

export class BombSystem {
  private bombs: Map<number, BombData> = new Map();
  private nextId: number = 1;
  private removalTimers: number[] = [];
  // Reference to the live game grid – updated externally by the scene.
  grid: CellType[][] = [];

  // -------------------------------------------------------------------------
  // Placement
  // -------------------------------------------------------------------------

  placeBomb(
    col: number,
    row: number,
    radius: number,
    ownerId: number,
    hasKick: boolean,
    remote: boolean,
  ): BombData | null {
    // Only one bomb per cell.
    if (this.getBombAt(col, row)) {
      return null;
    }

    const bomb: BombData = {
      id: this.nextId++,
      col,
      row,
      radius,
      ownerId,
      state: BombState.PLACED,
      timer: BOMB_FUSE_MS,
      hasKick,
      kickDirX: 0,
      kickDirY: 0,
      remote,
    };

    // Immediately arm the bomb.
    bomb.state = BombState.ARMED;

    this.bombs.set(bomb.id, bomb);

    events.emit('bomb:placed', { col, row, id: bomb.id });

    return bomb;
  }

  // -------------------------------------------------------------------------
  // Per-frame update
  // -------------------------------------------------------------------------

  /**
   * Ticks all ARMED bombs and returns those whose fuse has expired this frame.
   * State transitions (→ EXPLODING) are handled by the caller via explodeBomb().
   */
  update(delta: number): BombData[] {
    const toExplode: BombData[] = [];

    for (const bomb of this.bombs.values()) {
      if (bomb.state !== BombState.ARMED) continue;

      if (!bomb.remote) bomb.timer -= delta;

      if (bomb.timer <= 0) {
        toExplode.push(bomb);
      }
    }

    return toExplode;
  }

  // -------------------------------------------------------------------------
  // Explosion
  // -------------------------------------------------------------------------

  explodeBomb(id: number): ExplosionCell[] | null {
    const bomb = this.bombs.get(id);
    if (!bomb) return null;

    // Already exploding or removed – no-op.
    if (bomb.state === BombState.EXPLODING || bomb.state === BombState.REMOVED) {
      return null;
    }

    bomb.state = BombState.EXPLODING;

    const cells = this.calculateExplosion(bomb);

    // Process explosion effects on the grid and chain reactions.
    const destroyedCells: Array<{ col: number; row: number }> = [];

    for (const cell of cells) {
      const { col, row } = cell;

      if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) continue;

      const cellType = this.grid[row]?.[col];

      if (cellType === CellType.BLOCK) {
        // Destroy the block.
        this.grid[row][col] = CellType.FLOOR;
        destroyedCells.push({ col, row });
      }

      // Chain-reaction: detonate any ARMED bomb in this cell.
      const chainBomb = this.getBombAt(col, row);
      if (chainBomb && chainBomb.id !== id && chainBomb.state === BombState.ARMED) {
        // Force the timer to zero so it explodes next tick (avoids deep recursion
        // on large chain explosions while still feeling instantaneous).
        chainBomb.timer = 0;
      }
    }

    // Emit events.
    events.emit('bomb:exploded', { id, cells });

    for (const { col, row } of destroyedCells) {
      events.emit('block:destroyed', { col, row });
    }

    // Schedule removal after the visual duration.
    // Keep track of the timer so clear() can cancel it, preventing a stale
    // callback from touching a new BombSystem on the next level.
    const timerId = window.setTimeout(() => {
      this.removeBomb(id);
      const idx = this.removalTimers.indexOf(timerId);
      if (idx !== -1) this.removalTimers.splice(idx, 1);
    }, BOMB_EXPLODE_MS);
    this.removalTimers.push(timerId);

    return cells;
  }

  /**
   * Calculates the grid cells affected by a bomb explosion.
   * Follows classic Bomberman rules: stops at walls (exclusive) and blocks (inclusive).
   */
  calculateExplosion(bomb: BombData): ExplosionCell[] {
    const cells: ExplosionCell[] = [];
    const now = 0; // timer field is unused by callers; set to 0.

    // Center cell.
    cells.push({ col: bomb.col, row: bomb.row, type: 'center', timer: now });

    // Direction descriptors: [deltaCol, deltaRow, middleType, tipType]
    const directions: Array<[number, number, ExplosionCell['type'], ExplosionCell['type']]> = [
      [-1, 0, 'h', 'tip_left'],
      [1, 0, 'h', 'tip_right'],
      [0, -1, 'v', 'tip_up'],
      [0, 1, 'v', 'tip_down'],
    ];

    for (const [dc, dr, middleType, tipType] of directions) {
      for (let step = 1; step <= bomb.radius; step++) {
        const col = bomb.col + dc * step;
        const row = bomb.row + dr * step;

        if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) break;

        const cellType = this.grid[row]?.[col] ?? CellType.FLOOR;

        if (cellType === CellType.WALL) {
          // Hard wall – stop without including this cell.
          break;
        }

        const isTip = step === bomb.radius || cellType === CellType.BLOCK;
        const type: ExplosionCell['type'] = isTip ? tipType : middleType;

        cells.push({ col, row, type, timer: now });

        if (cellType === CellType.BLOCK) {
          // Soft block – include cell but do not continue further.
          break;
        }
      }
    }

    return cells;
  }

  // -------------------------------------------------------------------------
  // Remote detonation
  // -------------------------------------------------------------------------

  /**
   * Immediately detonates all remote bombs belonging to the given owner.
   */
  remoteDetonate(ownerId: number): void {
    for (const bomb of this.bombs.values()) {
      if (bomb.remote && bomb.ownerId === ownerId && bomb.state === BombState.ARMED) {
        bomb.timer = 0;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Bomb kick
  // -------------------------------------------------------------------------

  kickBomb(id: number, dirX: number, dirY: number): boolean {
    const bomb = this.bombs.get(id);
    if (!bomb || bomb.state !== BombState.ARMED || Math.abs(dirX) + Math.abs(dirY) !== 1) return false;
    const col = bomb.col + dirX, row = bomb.row + dirY;
    const cell = this.grid[row]?.[col];
    if (cell !== CellType.FLOOR && cell !== CellType.EXIT) return false;
    if (this.getBombAt(col, row)) return false;
    bomb.col = col; bomb.row = row;
    return true;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  getBombAt(col: number, row: number): BombData | undefined {
    for (const bomb of this.bombs.values()) {
      if (
        bomb.col === col &&
        bomb.row === row &&
        bomb.state !== BombState.REMOVED
      ) {
        return bomb;
      }
    }
    return undefined;
  }

  /**
   * Returns true when the cell at (col, row) will be caught in an explosion
   * within the next `timeAhead` milliseconds.
   * Used by enemy AI to build a danger map.
   */
  isCellDangerousAt(col: number, row: number, timeAhead: number): boolean {
    for (const bomb of this.bombs.values()) {
      if (bomb.state !== BombState.ARMED) continue;
      if (bomb.timer > timeAhead) continue;

      const cells = this.calculateExplosion(bomb);
      for (const cell of cells) {
        if (cell.col === col && cell.row === row) return true;
      }
    }
    return false;
  }

  /**
   * Returns the set of all cells currently threatened by active bombs.
   * Keys are "col,row" strings.
   */
  getDangerCells(): Set<string> {
    const danger = new Set<string>();

    for (const bomb of this.bombs.values()) {
      if (bomb.state !== BombState.ARMED && bomb.state !== BombState.EXPLODING) continue;

      const cells = this.calculateExplosion(bomb);
      for (const cell of cells) {
        danger.add(`${cell.col},${cell.row}`);
      }
    }

    return danger;
  }

  // -------------------------------------------------------------------------
  // Removal
  // -------------------------------------------------------------------------

  removeBomb(id: number): void {
    const bomb = this.bombs.get(id);
    if (bomb) {
      bomb.state = BombState.REMOVED;
      this.bombs.delete(id);
    }
  }

  // -------------------------------------------------------------------------
  // Accessors (useful for scene/entity code)
  // -------------------------------------------------------------------------

  getAllBombs(): IterableIterator<BombData> {
    return this.bombs.values();
  }

  getBombCount(): number {
    return this.bombs.size;
  }

  getBombsByOwner(ownerId: number): BombData[] {
    const result: BombData[] = [];
    for (const bomb of this.bombs.values()) {
      if (bomb.ownerId === ownerId) result.push(bomb);
    }
    return result;
  }

  /**
   * Clears all bombs and cancels any pending removal timers.
   * Must be called on scene teardown to prevent stale callbacks.
   */
  clear(): void {
    for (const id of this.removalTimers) window.clearTimeout(id);
    this.removalTimers = [];
    this.bombs.clear();
    this.nextId = 1;
  }
}
